import { useEffect, useMemo, useRef, useState } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { Reflector } from 'three/examples/jsm/objects/Reflector.js';
import { DIRECTIONS, type Mirror, type Vec3 } from '@qoobd/rules';
import { game, PLAYER, ROOM_SIZE, roomCenter } from '../game/controller';
import { useGame } from '../game/store';
import { audio } from '../audio/engine';
import { Armor, type FaceMode } from './Armor';
import { inkFigure } from './Ghost';

/**
 * Mirror rooms: a tarnished mirror on one closed wall of about one room in 200. It shows the
 * player their own armor and the avatar behind their visor. Rarely, when the player faces it
 * and holds still, the reflection is wrong for a quarter of a second, then snaps back.
 *
 * It never carries information: the rival is on a layer the mirror can't see, and the mirror
 * room's doorways are filled with haze in the reflection, so it never shows anything beyond
 * the room.
 */

/** Render layers. 0: everything. Only the mirror's camera sees SELF; only the player's sees MAIN_ONLY. */
export const LAYER_SELF = 1;
export const LAYER_MAIN_ONLY = 2;

const PANEL = 1.7;
const INSET = ROOM_SIZE / 2 - 0.18 - 0.02; // just in front of the wall's inner face
const FOG = 0x0e1013;
const CHANCE = { normal: 1 / 3, subtle: 1 / 6, off: 0 } as const;
const HOLD_MS = 1000;
const SCARE_MS = 250;
/**
 * The brief's wrong reflections: hollow eyes behind your visor, no one there at all, your
 * reflection with its back to you, or a figure standing just behind you.
 */
type Scare = 'hollow' | 'empty' | 'turned' | 'behind';
const SCARES: readonly Scare[] = ['hollow', 'empty', 'turned', 'behind'];
/** How far your reflection steps toward the glass as you face it squarely, so your face reads. */
const LEAN = 1.0;

const DEV = import.meta.env.DEV ? new URLSearchParams(location.search) : null;
const coarse = typeof matchMedia !== 'undefined' && matchMedia('(pointer: coarse)').matches;

const v3 = (d: Vec3) => new THREE.Vector3(d[0], d[1], d[2]);
const same = (a: Vec3, b: Vec3) => a[0] === b[0] && a[1] === b[1] && a[2] === b[2];

/** Pitted, cloudy tarnish laid over the glass. */
function tarnishTexture(): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = c.height = 256;
  const g = c.getContext('2d')!;
  let seed = 4242;
  const r = () => ((seed = (seed * 16807) % 2147483647) / 2147483647);
  for (let i = 0; i < 900; i++) {
    g.fillStyle = `rgba(40,34,26,${0.02 + r() * 0.08})`;
    g.beginPath();
    g.arc(r() * 256, r() * 256, 1 + r() * 9, 0, Math.PI * 2);
    g.fill();
  }
  const edge = g.createRadialGradient(128, 128, 70, 128, 128, 182);
  edge.addColorStop(0, 'rgba(30,26,20,0)');
  edge.addColorStop(1, 'rgba(30,26,20,0.75)');
  g.fillStyle = edge;
  g.fillRect(0, 0, 256, 256);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

/** The ghost's ink figure redrawn in pale bone, so it shows against a dark reflection. */
function boneFigure(): THREE.CanvasTexture {
  const src = inkFigure().image as HTMLCanvasElement;
  const c = document.createElement('canvas');
  c.width = src.width;
  c.height = src.height;
  const g = c.getContext('2d')!;
  g.drawImage(src, 0, 0);
  g.drawImage(src, 0, 0); // denser
  g.globalCompositeOperation = 'source-in';
  g.fillStyle = 'rgb(216,210,196)';
  g.fillRect(0, 0, c.width, c.height);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

function placeOnWall(obj: THREE.Object3D, mirror: Mirror, inset: number): void {
  const maze = game.match!.maze;
  const wall = v3(mirror.wall);
  roomCenter(maze, mirror.room, obj.position).addScaledVector(wall, inset);
  obj.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), wall.clone().negate());
}

function MirrorPanel({ mirror }: { mirror: Mirror }) {
  const group = useRef<THREE.Group>(null);
  const parts = useMemo(() => {
    const res = coarse ? 384 : 768;
    const glass = new Reflector(new THREE.PlaneGeometry(PANEL, PANEL), { textureWidth: res, textureHeight: res, color: 0x8d9298, clipBias: 0.003, multisample: 0 });
    glass.camera.layers.enable(LAYER_SELF);
    glass.camera.layers.disable(LAYER_MAIN_ONLY);
    const tarnish = new THREE.Mesh(
      new THREE.PlaneGeometry(PANEL, PANEL),
      new THREE.MeshBasicMaterial({ map: tarnishTexture(), transparent: true, depthWrite: false, fog: true }),
    );
    tarnish.position.z = 0.004;
    tarnish.layers.set(0);
    return { glass, tarnish };
  }, []);
  useEffect(() => {
    if (group.current) placeOnWall(group.current, mirror, INSET);
    return () => {
      parts.glass.dispose();
      parts.tarnish.geometry.dispose();
    };
  }, [mirror, parts]);
  const frame = 0.09;
  return (
    <group ref={group}>
      <primitive object={parts.glass} />
      <primitive object={parts.tarnish} />
      {[
        [0, PANEL / 2 + frame / 2, PANEL + frame * 2, frame],
        [0, -PANEL / 2 - frame / 2, PANEL + frame * 2, frame],
        [PANEL / 2 + frame / 2, 0, frame, PANEL],
        [-PANEL / 2 - frame / 2, 0, frame, PANEL],
      ].map(([x, y, w, h], i) => (
        <mesh key={i} position={[x!, y!, 0.02]}>
          <boxGeometry args={[w!, h!, 0.05]} />
          <meshStandardMaterial color={0x4a3f33} roughness={0.7} metalness={0.6} />
        </mesh>
      ))}
    </group>
  );
}

/** Haze over the mirror room's doorways, seen only in the reflection. */
function DoorHaze({ mirror }: { mirror: Mirror }) {
  const material = useMemo(() => new THREE.MeshBasicMaterial({ color: FOG, transparent: true, opacity: 0.94, side: THREE.DoubleSide, depthWrite: false }), []);
  const doors = useMemo(() => {
    const maze = game.match!.maze;
    return DIRECTIONS.filter((d) => {
      if (same(d, mirror.wall)) return false;
      const next = maze.neighbor(mirror.room, d);
      return next >= 0 && maze.hasDoor(mirror.room, next);
    });
  }, [mirror]);
  return (
    <>
      {doors.map((d, i) => (
        <mesh
          key={i}
          material={material}
          ref={(el) => {
            if (!el) return;
            placeOnWall(el, { room: mirror.room, wall: d }, ROOM_SIZE / 2 - 0.2);
            el.layers.set(LAYER_SELF);
          }}
        >
          <planeGeometry args={[1.9, 1.9]} />
        </mesh>
      ))}
    </>
  );
}

/** Development only: `?mirror` hangs a mirror in front of the player at the start and always scares. */
function devMirror(): Mirror | null {
  if (!DEV?.has('mirror')) return null;
  const a = game.match?.agent(PLAYER);
  return a ? { room: a.room, wall: a.orientation.facing } : null;
}

export function Mirrors() {
  const { camera } = useThree();
  const sceneVersion = useGame((s) => s.sceneVersion);
  const myArmor = useGame((s) => s.armor);
  const [scare, setScare] = useState<Scare | null>(null);
  const face: FaceMode = scare === 'hollow' ? 'hollow' : 'normal';
  const self = useRef<THREE.Group>(null);
  const fill = useRef<THREE.PointLight | null>(null);
  const figure = useRef<THREE.Mesh | null>(null);
  const figureMat = useMemo(() => new THREE.MeshBasicMaterial({ map: boneFigure(), transparent: true, opacity: 0.85, depthWrite: false, fog: true, side: THREE.DoubleSide }), []);
  const dev = useRef<{ match: unknown; mirror: Mirror | null }>({ match: null, mirror: null });
  const state = useRef({ match: null as unknown, used: false, holdKey: '', holdSince: 0, rolled: false, until: 0 });
  const tmp = useMemo(() => ({ up: new THREE.Vector3(), fwd: new THREE.Vector3() }), []);

  const m = game.match;
  if (m && dev.current.match !== m) dev.current = { match: m, mirror: devMirror() };
  const all = useMemo(() => {
    const list = [...(game.match?.mirrors ?? [])];
    if (dev.current.mirror) list.push(dev.current.mirror);
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [m]);
  const shown = useMemo(
    () => all.filter((x) => game.visible.has(x.room)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [all, sceneVersion],
  );

  useEffect(() => {
    camera.layers.enable(LAYER_MAIN_ONLY);
    camera.layers.disable(LAYER_SELF);
  }, [camera]);

  useFrame(() => {
    const match = game.match;
    if (!match || shown.length === 0) return;
    // Your reflection: your armor where you stand, helmet at eye height, facing where you look.
    tmp.up.set(0, 1, 0).applyQuaternion(camera.quaternion);
    camera.getWorldDirection(tmp.fwd);
    const me0 = match.agent(PLAYER);
    let square = 0;
    for (const x of all) {
      if (!me0 || x.room !== me0.room) continue;
      square = Math.max(square, tmp.fwd.dot(v3(x.wall)));
    }
    if (self.current) {
      // Nobody can see their own body, so the reflection can stand a little nearer the glass.
      self.current.position.copy(camera.position).addScaledVector(tmp.up, -0.49).addScaledVector(tmp.fwd, LEAN * Math.max(0, square) ** 6);
      self.current.quaternion.copy(camera.quaternion);
      if (scare === 'turned') self.current.rotateY(Math.PI);
      self.current.visible = scare !== 'empty';
    }
    // Your own lamp sits inside your helmet, so light your reflection from in front, for the glass only.
    if (fill.current) {
      fill.current.position.copy(camera.position).addScaledVector(tmp.fwd, 1.1);
    }

    const now = performance.now();
    const st = state.current;
    if (st.match !== match) Object.assign(st, { match, used: false, holdKey: '', holdSince: 0, rolled: false, until: 0 });

    if (st.until) {
      if (now < st.until) return;
      st.until = 0;
      setScare(null);
      if (figure.current) figure.current.visible = false;
      return;
    }
    if (st.used) return;

    const s = useGame.getState();
    const me = match.agent(PLAYER);
    const facing = me && !me.active ? all.find((x) => x.room === me.room && same(x.wall, me.orientation.facing)) : undefined;
    const quiet = s.phase === 'playing' && s.threatBand === 'none' && !match.viewFor(PLAYER).cloaked && s.atmosphere !== 'off';
    if (!facing || !quiet) {
      st.holdKey = '';
      return;
    }
    const key = `${facing.room}:${facing.wall.join(',')}`;
    if (st.holdKey !== key) {
      // A new stand in front of the mirror: one roll per visit.
      Object.assign(st, { holdKey: key, holdSince: now, rolled: false });
      return;
    }
    if (st.rolled || now - st.holdSince < HOLD_MS) return;
    st.rolled = true;
    if (!(DEV?.has('mirror') || Math.random() < CHANCE[s.atmosphere])) return;

    // Development only: ?mirror=hollow (or empty, turned, behind) picks the scare and holds it.
    const forced = DEV?.get('mirror') as Scare | undefined;
    const pinned = forced !== undefined && SCARES.includes(forced);
    const kind = pinned ? forced : SCARES[Math.floor(Math.random() * SCARES.length)]!;
    st.used = true;
    st.until = pinned ? Infinity : now + SCARE_MS;
    if (kind === 'behind') {
      const el = figure.current;
      if (el && self.current) {
        // A figure just behind your shoulder, facing the mirror; it's there only in the glass.
        el.position.copy(self.current.position).addScaledVector(tmp.fwd, -0.55).addScaledVector(tmp.up, 0.05);
        el.position.addScaledVector(new THREE.Vector3().crossVectors(tmp.fwd, tmp.up), 0.42);
        el.quaternion.copy(camera.quaternion);
        el.visible = true;
      }
    }
    setScare(kind);
    if (Math.random() < 0.35) audio.playDread(Math.random() < 0.5 ? 'breath' : 'whisper');
  });

  if (shown.length === 0) return null;
  return (
    <>
      {shown.map((x) => (
        <MirrorPanel key={`${x.room}:${x.wall.join(',')}`} mirror={x} />
      ))}
      {shown.map((x) => (
        <DoorHaze key={`h${x.room}:${x.wall.join(',')}`} mirror={x} />
      ))}
      <pointLight
        ref={(el) => {
          fill.current = el;
          el?.layers.set(LAYER_SELF);
        }}
        color={0xffe2b0}
        intensity={7}
        distance={4}
        decay={2}
      />
      <group ref={self}>
        <Armor armor={myArmor} side={0xffb000} face={face} hollow layer={LAYER_SELF} />
      </group>
      <mesh
        ref={(el) => {
          figure.current = el;
          el?.layers.set(LAYER_SELF);
        }}
        visible={false}
        material={figureMat}
      >
        <planeGeometry args={[1.0, 2.0]} />
      </mesh>
    </>
  );
}
